import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Search, Star, StarOff, Package } from "lucide-react";
import { toast } from "sonner";
import { api, asError } from "@/api/client";
import { normalizeImageUrl } from "@/lib/imageUrl";
import ImageUploader from "@/components/admin/ImageUploader";

const CATS = ["noodles", "pasta", "cookies", "snacks", "mixes", "staples"];

const EMPTY = {
  name: "", category: "noodles", image: "", weight: "", price: 0,
  tags: [], stock: 0, featured: false, active: true, description: "",
};

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("all");
  const [edit, setEdit] = useState(null); // null | EMPTY (create) | productObj (edit)

  const load = () => {
    setLoading(true);
    api.get("/products", { params: { only_active: false, limit: 100 } })
      .then(({ data }) => setItems(data.items || data))
      .catch(() => toast.error("Failed to load products"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = items.filter((p) =>
    (cat === "all" || p.category === cat) &&
    (!search || p.name.toLowerCase().includes(search.toLowerCase()))
  );

  const save = async (form) => {
    try {
      const payload = {
        ...form,
        price: Number(form.price) || 0,
        stock: Number(form.stock) || 0,
        tags: typeof form.tags === "string" ? form.tags.split(",").map(s => s.trim()).filter(Boolean) : form.tags,
      };
      if (form.id) {
        await api.patch(`/products/${form.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product created");
      }
      setEdit(null);
      load();
    } catch (e) {
      toast.error(asError(e));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch (e) { toast.error(asError(e)); }
  };

  const toggleFeatured = async (p) => {
    try {
      await api.patch(`/products/${p.id}`, { featured: !p.featured });
      load();
    } catch (e) { toast.error(asError(e)); }
  };

  return (
    <div data-testid="admin-products">
      <div className="flex flex-col md:flex-row md:items-center gap-3 md:justify-between mb-5">
        <div className="flex items-center gap-3 flex-1 max-w-xl">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)]" />
            <input
              data-testid="admin-products-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products…"
              className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-[#e5dec9] rounded-md focus:border-[var(--green)] outline-none"
            />
          </div>
          <select
            value={cat}
            onChange={(e) => setCat(e.target.value)}
            className="bg-white border border-[#e5dec9] rounded-md text-sm px-3 py-2 focus:border-[var(--green)] outline-none"
          >
            <option value="all">All categories</option>
            {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <button
          data-testid="admin-products-add"
          onClick={() => setEdit({ ...EMPTY })}
          className="btn-primary !py-2"
        >
          <Plus size={14} /> New Product
        </button>
      </div>

      <div className="bg-white border border-[#e5dec9] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-cream)] text-[var(--ink-3)] uppercase text-[10px] tracking-widest">
            <tr>
              <th className="text-left px-4 py-3 w-12">S.No</th>
              <th className="text-left">Product</th>
              <th className="text-left">Category</th>
              <th className="text-left">Price</th>
              <th className="text-left">Stock</th>
              <th className="text-left">Status</th>
              <th className="text-right px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center py-10 text-[var(--ink-3)] kbd text-xs">Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[var(--ink-3)] text-sm">No products match.</td></tr>}
            {filtered.map((p, idx) => (
              <tr key={p.id} data-testid={`admin-product-row-${p.id}`} className="border-t border-[#f0e7d3]">
                <td className="px-4 py-3 kbd text-[0.7rem] text-[var(--ink-3)]">{idx + 1}</td>
                <td className="py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md overflow-hidden bg-[var(--bg-cream)] flex items-center justify-center">
                      {p.image ? <img src={normalizeImageUrl(p.image)} alt="" className="w-full h-full object-cover" /> : <Package size={14} className="text-[var(--ink-3)]" />}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-[var(--ink)] truncate">{p.name}</div>
                      <div className="text-[var(--ink-3)] text-xs">{p.weight}</div>
                    </div>
                  </div>
                </td>
                <td className="text-[var(--ink-2)] capitalize">{p.category}</td>
                <td className="font-semibold text-[var(--green)]">₹{p.price}</td>
                <td className={`font-semibold ${p.stock <= 5 ? "text-red-600" : p.stock <= 20 ? "text-[var(--gold)]" : "text-[var(--ink)]"}`}>
                  {p.stock}
                </td>
                <td>
                  {p.active
                    ? <span className="text-[10px] uppercase tracking-widest font-semibold text-[var(--green)] bg-green-50 border border-green-200 rounded-full px-2 py-1">Active</span>
                    : <span className="text-[10px] uppercase tracking-widest font-semibold text-gray-500 bg-gray-100 border border-gray-200 rounded-full px-2 py-1">Inactive</span>}
                </td>
                <td className="px-4 text-right whitespace-nowrap">
                  <button onClick={() => toggleFeatured(p)} title={p.featured ? "Unfeature" : "Feature"} className="p-1.5 hover:bg-[var(--bg-cream)] rounded">
                    {p.featured ? <Star size={15} className="text-[var(--gold)] fill-[var(--gold)]" /> : <StarOff size={15} className="text-[var(--ink-3)]" />}
                  </button>
                  <button onClick={() => setEdit(p)} data-testid={`admin-product-edit-${p.id}`} className="p-1.5 hover:bg-[var(--bg-cream)] rounded">
                    <Pencil size={15} className="text-[var(--green)]" />
                  </button>
                  <button onClick={() => remove(p)} className="p-1.5 hover:bg-red-50 rounded">
                    <Trash2 size={15} className="text-red-600" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {edit && <ProductDialog initial={edit} onClose={() => setEdit(null)} onSave={save} />}
    </div>
  );
}

function ProductDialog({ initial, onClose, onSave }) {
  const [f, setF] = useState({
    ...initial,
    tags: Array.isArray(initial.tags) ? initial.tags.join(", ") : (initial.tags || ""),
  });

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div data-testid="admin-product-dialog" className="relative bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e5dec9] sticky top-0 bg-white">
          <h3 className="font-serif-display text-xl text-[var(--ink)]">{f.id ? "Edit Product" : "New Product"}</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <form className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4"
          onSubmit={(e) => { e.preventDefault(); onSave(f); }}
        >
          <Inp label="Name *" value={f.name} onChange={(v) => setF({ ...f, name: v })} testId="dlg-name" />
          <div>
            <Lbl>Category</Lbl>
            <select className="adminInp" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
              {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <Lbl>Product Image</Lbl>
            <ImageUploader value={f.image} onChange={(v) => setF({ ...f, image: v })} />
          </div>
          <Inp label="Weight (e.g. 200g)" value={f.weight} onChange={(v) => setF({ ...f, weight: v })} />
          <Inp label="Price ₹" type="number" value={f.price} onChange={(v) => setF({ ...f, price: v })} testId="dlg-price" />
          <Inp label="Stock" type="number" value={f.stock} onChange={(v) => setF({ ...f, stock: v })} />
          <Inp label="Tags (comma separated)" value={f.tags} onChange={(v) => setF({ ...f, tags: v })} />
          <div className="flex items-center gap-5 mt-6">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />
              Active
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} />
              Featured
            </label>
          </div>
          <div className="md:col-span-2">
            <Lbl>Description</Lbl>
            <textarea rows={4} className="adminInp resize-none" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
          </div>
          <div className="md:col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
            <button type="submit" data-testid="dlg-save" className="btn-primary">{f.id ? "Save Changes" : "Create Product"}</button>
          </div>
        </form>
      </div>
      <style>{`.adminInp{width:100%;background:#fff;border:1px solid #e5dec9;border-radius:.375rem;padding:.55rem .75rem;font-size:.875rem;outline:none;font-family:inherit}.adminInp:focus{border-color:var(--green)}`}</style>
    </div>
  );
}

function Lbl({ children }) { return <label className="kbd text-[0.6rem] text-[var(--ink-3)] mb-1.5 block">{children}</label>; }
function Inp({ label, value, onChange, type = "text", testId }) {
  return (
    <div>
      <Lbl>{label}</Lbl>
      <input
        type={type}
        data-testid={testId}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="adminInp"
      />
    </div>
  );
}
